// Summary view of the core (В14): records of the view counted by one field in rows and another in columns — stage × track,
// status × event; a cell opens the table of exactly those records. Any type, any taxonomy, relation, user or checkbox field.

import { useMemo } from 'react';
import { recordTitle, type FieldDef, type Filter, type Id, type RecordObj, type ViewDef } from '@confdeck/core-shared';
import { useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { Select } from '../ui/primitives.tsx';
import { plural } from '../table/DataTable.tsx';
import type { DrillChip } from '../table/view-state.ts';
import s from './summary.module.css';

const NONE = '';
const GROUPABLE = new Set(['taxonomy', 'relation', 'user', 'checkbox']);

type Axis = { field: FieldDef; values: { key: string; label: string; rank: number }[] };

export function Summary({ typeId, view, ids, onAxes, onCell }: {
  typeId: Id; view: ViewDef; ids: Id[];
  onAxes: (summary: NonNullable<ViewDef['summary']>) => void;
  /** A number was clicked: the table of exactly those records, with the conditions to show as chips. */
  onCell: (filter: Filter, chips: DrillChip[]) => void;
}) {
  const meta = useMeta();
  const client = useSync();
  useTypesTick([typeId]);
  const fields = meta.fields(typeId).filter((f) => !f.archivedAt && GROUPABLE.has(f.fieldType));
  const rowsField = meta.field(typeId, view.summary?.rows ?? '') ?? fields[0];
  const colsField = view.summary?.cols ? meta.field(typeId, view.summary.cols) : undefined;

  /** The group keys of a record by a field: a record may sit in several (a multiple taxonomy, many links); none — «Не указано». */
  const keysOf = (rec: RecordObj, f: FieldDef): string[] => {
    if (f.fieldType === 'relation') { const l = client.store.linked(rec, f); return l.length ? l : [NONE]; }
    const v = rec.data[f.key];
    if (f.fieldType === 'checkbox') return [v === true ? 'yes' : 'no'];
    const list = (Array.isArray(v) ? v : [v]).filter((x) => x !== null && x !== undefined && x !== '').map(String);
    return list.length ? list : [NONE];
  };
  const labelOf = (f: FieldDef, key: string): { label: string; rank: number } => {
    if (key === NONE) return { label: 'Не указано', rank: Number.MAX_SAFE_INTEGER };
    if (f.fieldType === 'taxonomy') return { label: meta.term(key)?.name ?? '—', rank: meta.termRank(key) };
    if (f.fieldType === 'user') return { label: meta.user(key)?.name ?? '—', rank: 0 };
    if (f.fieldType === 'checkbox') return { label: key === 'yes' ? 'Да' : 'Нет', rank: key === 'yes' ? 0 : 1 };
    const r = client.store.record(key);
    return { label: r ? recordTitle(r, meta) : '—', rank: 0 };
  };

  const { rowAxis, colAxis, cells, total } = useMemo(() => {
    const cells = new Map<string, number>();
    const rowKeys = new Set<string>(), colKeys = new Set<string>();
    for (const id of ids) {
      const rec = client.store.record(id);
      if (!rec || !rowsField) continue;
      for (const rk of keysOf(rec, rowsField)) {
        rowKeys.add(rk);
        for (const ck of colsField ? keysOf(rec, colsField) : ['*']) { colKeys.add(ck); cells.set(`${rk}|${ck}`, (cells.get(`${rk}|${ck}`) ?? 0) + 1); }
      }
    }
    const axis = (f: FieldDef | undefined, keys: Set<string>): Axis | null => f ? {
      field: f, values: [...keys].map((k) => ({ key: k, ...labelOf(f, k) })).sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label, 'ru')),
    } : null;
    return { rowAxis: axis(rowsField, rowKeys), colAxis: axis(colsField, colKeys), cells, total: ids.length };
  }, [ids, rowsField?.id, colsField?.id, client.store, meta]);

  /** The condition a cell stands for: the same value of the field, or an empty field. */
  const cond = (f: FieldDef, key: string): Filter => key === NONE ? { field: f.key, op: 'empty' }
    : f.fieldType === 'relation' ? { field: f.key, op: 'some', where: { field: 'id', op: 'eq', value: key } }
    : f.fieldType === 'checkbox' ? (key === 'yes' ? { field: f.key, op: 'eq', value: true } : { not: { field: f.key, op: 'eq', value: true } })
    : { field: f.key, op: 'eq', value: key };
  const open = (rk: string, ck: string | null) => {
    const parts = [view.filter, rowAxis && cond(rowAxis.field, rk), ck !== null && colAxis ? cond(colAxis.field, ck) : null].filter((x): x is Filter => !!x);
    const chips: DrillChip[] = [];
    if (rowAxis) chips.push({ label: rowAxis.field.name, value: rowAxis.values.find((v) => v.key === rk)?.label ?? '—' });
    if (ck !== null && colAxis) chips.push({ label: colAxis.field.name, value: colAxis.values.find((v) => v.key === ck)?.label ?? '—' });
    onCell(parts.length === 1 ? parts[0]! : { and: parts }, chips);
  };
  /** A number is a button that says what it opens (owner 29.10, п.5). */
  const num = (v: number, rk: string, ck: string | null) => {
    const hint = `Открыть ${v.toLocaleString('ru-RU')} ${plural(v, 'запись', 'записи', 'записей')}`;
    return <button key={`${rk}|${ck}`} type="button" className={s.cell} title={hint} aria-label={hint} onClick={() => open(rk, ck)}>{v.toLocaleString('ru-RU')}</button>;
  };
  const n = (rk: string, ck: string) => cells.get(`${rk}|${ck}`) ?? 0;
  const cols = colAxis?.values ?? [{ key: '*', label: 'Записей', rank: 0 }];

  return (
    <div className={s.wrap}>
      <div className={s.bar}>
        <label className={s.pick}>Строки
          <Select label="Строки" value={rowsField?.key ?? ''} onChange={(v) => onAxes({ rows: v, cols: colsField?.key ?? null })} options={fields.map((f) => ({ value: f.key, label: f.name }))} />
        </label>
        <label className={s.pick}>Колонки
          <Select label="Колонки" value={colsField?.key ?? ''} onChange={(v) => onAxes({ rows: rowsField?.key ?? '', cols: v || null })} options={[{ value: '', label: '— только итог' }, ...fields.filter((f) => f.id !== rowsField?.id).map((f) => ({ value: f.key, label: f.name }))]} />
        </label>
        <span className={s.total}>Всего: {total}</span>
      </div>
      {!rowAxis ? <div className={s.empty}>В разделе нет полей для сводки: справочника, связи или пользователя.</div> : (
        <div className={s.scroll}>
          <table className={s.table}>
            <thead><tr><th>{rowAxis.field.name}{colAxis ? ` × ${colAxis.field.name}` : ''}</th>{cols.map((c) => <th key={c.key} className={s.num}>{c.label}</th>)}{colAxis && <th className={s.num}>Итого</th>}</tr></thead>
            <tbody>
              {rowAxis.values.map((r) => (
                <tr key={r.key}>
                  <th>{r.label}</th>
                  {cols.map((c) => { const v = n(r.key, c.key); return <td key={c.key} className={s.num}>{v ? num(v, r.key, colAxis ? c.key : null) : <span className={s.zero}>·</span>}</td>; })}
                  {colAxis && <td className={s.num}>{num(cols.reduce((a, c) => a + n(r.key, c.key), 0), r.key, null)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          <p className={s.note}>Нажмите на число, чтобы открыть эти записи таблицей. Запись с несколькими значениями поля считается в каждой своей строке или колонке.</p>
        </div>
      )}
    </div>
  );
}
